// ============================================================================
// route-links — pure helpers for the Route Optimizer's Google Maps handoff
// and manual-order leg arithmetic (2026-07-31 run, D-1/D-3).
//
// PLAIN URLS ONLY — the directions deep link needs no API key (same pattern
// as FacilitySheet's "Navigate here"). Distances are haversine, labeled
// "approx" in the UI (D-1: straight-line; Routes API minutes = fast-follow).
// ============================================================================

import { haversineMiles, type RouteStop } from './route-order';

export interface LatLng {
  lat: number;
  lng: number;
}

/** Google Maps directions URLs cap at 9 waypoints + destination. */
export const MAX_MAPS_LINK_STOPS = 10;

export type HandoffMode = 'single-link' | 'next-stop';

/** D-3: ≤10 stops get one Maps link; longer routes drive leg-by-leg. */
export function handoffModeFor(stopCount: number): HandoffMode {
  return stopCount <= MAX_MAPS_LINK_STOPS ? 'single-link' : 'next-stop';
}

const BASE = 'https://www.google.com/maps/dir/?api=1';

const coord = (p: LatLng): string => `${p.lat},${p.lng}`;

/**
 * One directions URL with the stops in the GIVEN order (never reorders).
 * origin = user location when known (omitted otherwise — Google then uses
 * the device's current position), destination = last stop, everything in
 * between as ordered waypoints.
 */
export function buildDirectionsUrl(stops: LatLng[], origin?: LatLng): string {
  if (stops.length === 0) {
    throw new RangeError('buildDirectionsUrl needs at least one stop');
  }
  if (stops.length > MAX_MAPS_LINK_STOPS) {
    throw new RangeError(
      `Google Maps links support at most ${MAX_MAPS_LINK_STOPS} stops ` +
        `(got ${stops.length}) — use next-stop mode`,
    );
  }
  const params = new URLSearchParams();
  if (origin) params.set('origin', coord(origin));
  params.set('destination', coord(stops[stops.length - 1]));
  if (stops.length > 1) {
    params.set(
      'waypoints',
      stops.slice(0, -1).map(coord).join('|'),
    );
  }
  return `${BASE}&${params.toString()}`;
}

/** Per-leg launch for next-stop mode: plain single-destination link
 *  (navigation starts from wherever the driver actually is). */
export function buildLegUrl(stop: LatLng): string {
  const params = new URLSearchParams();
  params.set('destination', coord(stop));
  return `${BASE}&${params.toString()}`;
}

export interface OrderLeg {
  fromId: string | null;
  toId: string;
  miles: number;
}

/**
 * Per-leg miles + total for an EXPLICIT order (manual reorder wins — this
 * never re-optimizes; that is the whole point). Unknown ids in `orderedIds`
 * are skipped defensively (stale id after a remove).
 */
export function legsForOrder(
  stops: RouteStop[],
  orderedIds: string[],
  start?: LatLng | null,
): { legs: OrderLeg[]; totalMiles: number } {
  const byId = new Map(stops.map((s) => [s.id, s]));
  const seq = orderedIds
    .map((id) => byId.get(id))
    .filter((s): s is RouteStop => s !== undefined);

  const legs: OrderLeg[] = [];
  let totalMiles = 0;
  let prev: { id: string | null; lat: number; lng: number } | null = start
    ? { id: null, lat: start.lat, lng: start.lng }
    : null;

  for (const s of seq) {
    const miles = prev
      ? haversineMiles(prev.lat, prev.lng, s.lat, s.lng)
      : 0;
    legs.push({ fromId: prev ? prev.id : null, toId: s.id, miles });
    totalMiles += miles;
    prev = { id: s.id, lat: s.lat, lng: s.lng };
  }
  return { legs, totalMiles };
}
